import { useEffect, useState } from 'react';
import { Icon, cn } from '@dataroom/shared';
import { checklistSetupSteps, formatElapsed, type ChecklistSetupStage } from '@/lib/checklist-progress';

/** After this long the panel says a big checklist takes a while (est.: a full
 * checklist is ~1,200 sequential database writes in one request). */
const SLOW_AFTER_S = 15;

/**
 * Progress while a room's checklist is set up (owner ask 2026-10-12: "it can
 * be a little lengthy — we need some sort of progress indicator"). The server
 * builds the checklist in ONE request and cannot report rows done, so this is
 * an honest stage indicator — which step is running, with a live elapsed clock
 * and a moving bar — never a frozen screen.
 */
export function ChecklistSetupProgress({
  stage,
  startedAt,
  upload,
  expecting,
}: {
  stage: ChecklistSetupStage;
  /** `Date.now()` when the setup started. */
  startedAt: number;
  /** An uploaded workbook (adds the "read your file" step). */
  upload: boolean;
  /** What is being created, when known up-front (a saved checklist's counts). */
  expecting?: string;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);
  const elapsed = (now - startedAt) / 1_000;
  const steps = checklistSetupSteps(upload);
  const current = steps.findIndex((s) => s.stage === stage);

  return (
    <section
      className="space-y-3 rounded-lg border border-input p-4"
      aria-label="Setting up the checklist"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium">Setting up the checklist…</p>
        <p className="text-xs tabular-nums text-muted-foreground" aria-hidden="true">
          {formatElapsed(elapsed)} elapsed
        </p>
      </div>
      <ol className="space-y-2" aria-label="Steps">
        {steps.map((s, i) => {
          const state = i < current ? 'done' : i === current ? 'active' : 'pending';
          return (
            <li
              key={s.stage}
              className={cn(
                'flex items-start gap-2 text-sm',
                state === 'pending' && 'text-muted-foreground',
              )}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span className="mt-0.5">
                {state === 'done' ? (
                  <Icon name="check" size={14} className="text-primary" />
                ) : state === 'active' ? (
                  <Icon name="spinner" size={14} className="animate-spin text-primary" />
                ) : (
                  <span className="block h-3.5 w-3.5 rounded-full border border-input" />
                )}
              </span>
              <span>
                {s.label}
                {state === 'done' ? <span className="sr-only"> (done)</span> : null}
              </span>
            </li>
          );
        })}
      </ol>
      <div
        className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={`Step ${current + 1} of ${steps.length}: ${steps[current]?.label ?? ''}`}
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={current + 1}
      >
        {/* Completed steps fill solid; the running step shimmers, so the screen
            visibly moves even though one request cannot report row counts. */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary"
          style={{ width: `${(current / steps.length) * 100}%` }}
        />
        <div
          className="absolute inset-y-0 animate-pulse rounded-full bg-primary/50"
          style={{
            left: `${(current / steps.length) * 100}%`,
            width: `${100 / steps.length}%`,
          }}
        />
      </div>
      <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
        {expecting ? `Creating ${expecting}. ` : ''}
        {elapsed >= SLOW_AFTER_S
          ? 'Still working — a full checklist has hundreds of document requests and can take a minute. Keep this window open.'
          : 'Keep this window open.'}
      </p>
    </section>
  );
}
